import { Link } from "react-router-dom";
import { Eye, PackageCheck, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

const POINTS = [
  { icon: Eye, t: "You can watch it happen", d: "Witnessed certification means the evaluation isn't hidden behind a mail room. At MAS on-site events, you hand over your card and follow the process in person." },
  { icon: ShieldCheck, t: "Chain of custody stays intact", d: "Your card never disappears into an unmarked queue. From intake to sealed slab, the handling is transparent and accountable." },
  { icon: Clock, t: "Same-day, not same-month", d: "Instead of waiting weeks for a mail-in return, witnessed on-site grading can put a sealed, certified slab in your hands the same day." },
  { icon: PackageCheck, t: "Verifiable the moment it's done", d: "Every card leaves with a unique cert number and a live population entry you — and any future buyer — can verify online instantly." },
];

const FAQ = [
  ["What is witnessed certification?", "Witnessed certification is grading and authentication performed transparently, where the submitter can observe the process rather than mailing an item away sight-unseen. It's the principle MAS was built on and now brings to card grading on-site at conventions."],
  ["Can I watch my cards being graded?", "At MAS on-site convention events, you drop your cards at the booth and follow the process in person, with real-time SMS and email updates so you know the moment they're ready for pickup."],
  ["Is a witnessed grade still verifiable later?", "Yes. Every MAS grade — on-site or mail-in — is sealed in a tamper-evident slab with a unique certification number that anyone can verify online, backed by a live public population registry."],
];

const LD = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Witnessed Certification: Card Grading You Can Actually Watch",
    description: "What witnessed certification means and why it matters — transparent, in-person card grading with same-day, verifiable results from MAS Grading.",
    author: { "@type": "Organization", name: "MAS Grading" },
    publisher: { "@type": "Organization", name: "MAS Grading", logo: { "@type": "ImageObject", url: "https://grading.mascertified.com/mas-logo-black.png" } },
    mainEntityOfPage: { "@type": "WebPage", "@id": "https://grading.mascertified.com/witnessed-certification" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
];

export default function WitnessedCert() {
  return (
    <div className="bg-white" data-testid="witnessed-page">
      <Seo
        title="Witnessed Certification — Card Grading You Can Watch"
        description="Witnessed certification means grading you can observe in person — transparent chain of custody, same-day results, and a cert anyone can verify online. The MAS difference, explained."
        path="/witnessed-certification"
        type="article"
        jsonLd={LD}
      />
      <PageHeader
        eyebrow="Witnessed Certification"
        title="Grading you can watch — not mail away and hope"
        subtitle="Witnessed certification is the principle MAS was built on: transparent, in-person evaluation with results you can verify instantly. Here's what it means for your cards."
        word="SEEN"
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-5">
          {POINTS.map((p) => (
            <div key={p.t} className="rounded-3xl border border-neutral-200 bg-white p-8">
              <p.icon className="text-[#0A0A0A] mb-5" size={28} />
              <h3 className="font-display font-extrabold text-2xl tracking-tight text-[#0A0A0A]">{p.t}</h3>
              <p className="text-neutral-500 mt-3 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-[#0A0A0A]">Frequently asked</h2>
          <div className="mt-8 space-y-6">
            {FAQ.map(([q, a]) => (
              <div key={q} className="rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-6 md:p-8">
                <h3 className="font-display font-bold text-lg text-[#0A0A0A]">{q}</h3>
                <p className="text-neutral-500 mt-2 leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-[#0A0A0A] text-white p-8 md:p-12 mt-16">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">See witnessed grading in action</h2>
          <p className="text-white/60 mt-4 max-w-2xl leading-relaxed">MAS is taking on-site grading on the road — trials begin Q4 2026 with a national rollout in 2027. Read the announcement, or submit your cards online today.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/press" data-testid="witnessed-cta-press" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0A0A0A] font-bold rounded-full hover:bg-neutral-200 transition-colors duration-300">
              Read the announcement <ArrowRight size={16} />
            </Link>
            <Link to="/submit" data-testid="witnessed-cta-submit" className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-bold rounded-full hover:border-white transition-colors duration-300">
              Submit an Item
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
